import React, { useState, useEffect, useRef } from "react";
import "../CSS/CreateReportForm.css";
import Sidebar from "../Component/Sidebar";

// Redux stuff
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

// Thunks for reports
import {
  fetchReportThunkById,
  createReportThunk,
  updateReportThunk,
  fetchReportCommentsThunk,
  addReportCommentThunk,
} from "../features/reports/reportThunks";

// Thunk for stores
import {
  fetchStoresThunk,
  fetchStoreNotesThunk
} from "../features/stores/storeThunks";

// Import employee thunk to fetch employee list
import { fetchEmployeesThunk } from "../features/employees/employeeThunks";

// ============ COMPRESSION IMPORTS ============
import imageCompression from 'browser-image-compression';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
// ==============================================

// ============ ROLE-BASED PERMISSIONS ============
// Role rank constants (matching backend)
const ROLE_RANKS = {
  'ADMIN': 5,
  'TECH': 5,
  'TEAM_LEADER': 4,
  'HR': 4,
  'SALES': 4,
  'QUALITY': 3,
  'SPA': 2,
  'PA': 1,
};

// Helper function to get user rank
const getUserRank = (user) => {
  if (!user || !user.employee_profile) return 0;
  const role = user.employee_profile.role;
  return ROLE_RANKS[role] || 0;
};

// Helper function to check if user is PA
const isPA = (user) => {
  return getUserRank(user) === 1;
};

// Helper function to check if user is SPA or above
const isSPAOrAbove = (user) => {
  return getUserRank(user) >= 2;
};

// Helper function to check if user is team leader or above
const isTeamLeaderOrAbove = (user) => {
  return getUserRank(user) >= 4;
};
// ================================================

function CreateEditReportForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Are we editing or creating?
  const { reportId } = useParams();
  const editMode = Boolean(reportId);

  // Redux states
  const { reports, loading: reportsLoading } = useSelector(
    (state) => state.reports
  );
  const { stores, loading: storesLoading } = useSelector((state) => state.stores);
  const { employees, loading: employeesLoading } = useSelector(
    (state) => state.employees
  );
  const { user } = useSelector((state) => state.auth);

  // Store notes from Redux
  const storeNotes = useSelector((state) => state.stores.notesData.selectedStoreNotes);
  const notesLoading = useSelector((state) => state.stores.notesData.loading);

  // Comments from Redux
  const commentsState = useSelector((state) =>
    state.reports.comments.byReportId[reportId] || { comments: [], loading: false, error: null }
  );
  const { addingComment, addCommentError } = useSelector((state) => state.reports.comments);

  // We assume user.employee_profile.id is the employee ID
  const employeeId = user?.employee_profile?.id;

  // Role-based permissions
  const userIsPA = isPA(user);
  const userIsSPAOrAbove = isSPAOrAbove(user);
  const userIsTeamLeaderOrAbove = isTeamLeaderOrAbove(user);
  const userRank = getUserRank(user);

  // Attempt to find the existing report in Redux
  const existingReport = editMode
    ? reports.find((r) => r.id === parseInt(reportId))
    : null;

  // Initial form state
  const initialFormData = {
    incident_date: "",
    incident_time: null,
    store: "",
    details: "",
    call_status: "",
    reason_called: "",
    reason_not_called: "",
    severity: "",
    announcement_by: null,
    customer_category: "",
    dress: "",
    product: "",
    pickup_time: null,
    hide_time: null,
    exit_time: null,
    status: "SUBMITTED_REVIEW",
    incident_type: "theftPrevented",
  };

  // Local form state
  const [formData, setFormData] = useState(initialFormData);

  // Separate states for existing and new files
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  
  // ============ NEW: Preview URLs for new files ============
  const [newFilePreviewUrls, setNewFilePreviewUrls] = useState([]);
  const newFilePreviewUrlsRef = useRef([]);
  // =========================================================
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // Upload queue states - FIX for race condition
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  // Comment state
  const [newComment, setNewComment] = useState("");
  
  // NEW: Mention states
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [cursorPosition, setCursorPosition] = useState(0);
  const commentInputRef = useRef(null);

  // NEW: Store search and notes modal states
  const [storeSearch, setStoreSearch] = useState("");
  const [showStoreNotesModal, setShowStoreNotesModal] = useState(false);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    confirmAction: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Action menu state
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const actionMenuRef = useRef(null);
  const storeDropdownRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  // ============ COMPRESSION STATES ============
  const [ffmpeg] = useState(() => new FFmpeg());
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState('');
  // ============================================

  // Reason arrays
  const reasonCalledOptions = [
    "Able to stop",
    "Customer denied",
    "Cashier didn't ask the customer",
    "Customer left the store immediately",
    "Cashier Fraud",
    "Other",
  ];
  const reasonNotCalledOptions = [
    "Network issue",
    "Not sure about stealing",
    "Customer left the store immediately",
    "Cashier Suspicious Activity",
    "Cashier Opened the store",
    "Cashier Closed the store",
    "Other",
  ];

  // Filter stores based on search
  const filteredStores = stores.filter(store =>
    store?.store_name?.toLowerCase().includes(storeSearch.toLowerCase()) ||
    store?.store_city?.toLowerCase().includes(storeSearch.toLowerCase())
  );

  // Get selected store details
  const selectedStore = stores.find(s => s.id === formData.store);

  // NEW: Filter employees for mention dropdown
  const filteredEmployeesForMention = employees.filter(emp => {
    const fullName = `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.toLowerCase();
    const username = emp.user?.username?.toLowerCase() || '';
    const search = mentionSearch.toLowerCase();
    return fullName.includes(search) || username.includes(search);
  });

  // Modal functions
  const showModal = (type, title, message, confirmAction = null, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      confirmAction,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.confirmAction) {
      modalState.confirmAction();
    }
    closeModal();
  };

  // NEW: Handle store notes modal
  const handleShowStoreNotes = () => {
    if (!formData.store) {
      showModal('error', 'Error', 'Please select a store first');
      return;
    }

    dispatch(fetchStoreNotesThunk(formData.store));
    setShowStoreNotesModal(true);
  };

  const handleCloseStoreNotesModal = () => {
    setShowStoreNotesModal(false);
  };

  // Helper functions
  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    return `incident-report-status-${statusLower}`;
  };

  const getFileNameFromUrl = (url) => {
    return url.split('/').pop().split('?')[0];
  };

  const getFileTypeFromUrl = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'py', 'js', 'css', 'html'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (documentExtensions.includes(extension)) return 'document';
    return 'document';
  };

  // NEW: Parse comment text to highlight mentions
  const parseCommentText = (text) => {
    const mentionPattern = /@\[([^\]]+)\]\((\d+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add mention
      parts.push({
        type: 'mention',
        name: match[1],
        id: match[2]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // NEW: Handle comment input change with mention detection
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setNewComment(value);
    setCursorPosition(cursorPos);

    // Check if user is typing a mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      
      // Check if there's a space after @ (which would end the mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionSearch(textAfterAt);
        setShowMentionDropdown(true);
        
        // Calculate dropdown position
        if (commentInputRef.current) {
          const textarea = commentInputRef.current;
          const textareaRect = textarea.getBoundingClientRect();
          
          // Approximate position (you might need to fine-tune this)
          setMentionPosition({
            top: textareaRect.bottom + window.scrollY,
            left: textareaRect.left + window.scrollX
          });
        }
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // NEW: Handle employee mention selection
  const handleMentionSelect = (employee) => {
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const textAfterCursor = newComment.substring(cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    const employeeName = `${employee.user?.first_name || ''} ${employee.user?.last_name || ''}`.trim();
    const mention = `@[${employeeName}](${employee.id})`;
    
    const newText = 
      newComment.substring(0, lastAtSymbol) + 
      mention + 
      ' ' +
      textAfterCursor;
    
    setNewComment(newText);
    setShowMentionDropdown(false);
    setMentionSearch('');
    
    // Focus back on textarea
    if (commentInputRef.current) {
      commentInputRef.current.focus();
      const newCursorPos = lastAtSymbol + mention.length + 1;
      setTimeout(() => {
        commentInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(false);
      }
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target)) {
        setStoreDropdownOpen(false);
      }
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(event.target)) {
        setShowMentionDropdown(false);
      }
    };

    if (actionMenuOpen || storeDropdownOpen || showMentionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionMenuOpen, storeDropdownOpen, showMentionDropdown]);
  
  // ============ NEW: Keep a ref of preview URLs so we can clean them on unmount ============
  useEffect(() => {
    newFilePreviewUrlsRef.current = newFilePreviewUrls;
  }, [newFilePreviewUrls]);

  // ============ NEW: Cleanup preview URLs when component unmounts ============
  useEffect(() => {
    return () => {
      newFilePreviewUrlsRef.current.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);
  // ===========================================================================

  // Fetch stores and employees on mount
  useEffect(() => {
    dispatch(fetchStoresThunk());
    dispatch(fetchEmployeesThunk());
  }, [dispatch]);

  // ============ LOAD FFMPEG ON MOUNT ============
  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        
        ffmpeg.on('log', ({ message }) => {
          console.log('FFmpeg:', message);
        });
        
        ffmpeg.on('progress', ({ progress }) => {
          const percent = Math.round(progress * 100);
          setCompressionProgress(percent);
        });

        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        
        setFfmpegLoaded(true);
        console.log('✅ FFmpeg loaded successfully');
      } catch (error) {
        console.error('❌ FFmpeg loading error:', error);
        setFfmpegLoaded(false);
      }
    };
    
    loadFFmpeg();
  }, [ffmpeg]);
  // ===============================================

  // If edit mode, fetch the existing report if not in Redux
  useEffect(() => {
    if (editMode && !existingReport) {
      dispatch(fetchReportThunkById(reportId));
    }
  }, [editMode, existingReport, reportId, dispatch]);

  // Fetch comments when in edit mode
  useEffect(() => {
    if (editMode && reportId) {
      dispatch(fetchReportCommentsThunk(reportId));
    }
  }, [editMode, reportId, dispatch]);

  // Populate local state from existing report
  useEffect(() => {
    if (editMode && existingReport) {
      setFormData({
        incident_date: existingReport.incident_date || "",
        incident_time: existingReport.incident_time || null,
        store: existingReport.store?.id || "",
        details: existingReport.details || "",
        call_status: existingReport.call_status || "",
        reason_called: existingReport.reason_called || "",
        reason_not_called: existingReport.reason_not_called || "",
        severity: existingReport.severity || "",
        announcement_by: existingReport.announcement_by || null,
        customer_category: existingReport.customer_category || "",
        dress: existingReport.dress || "",
        product: existingReport.product || "",
        pickup_time: existingReport.pickup_time || null,
        hide_time: existingReport.hide_time || null,
        exit_time: existingReport.exit_time || null,
        status: existingReport.status || "SUBMITTED_REVIEW",
        incident_type: existingReport.incident_type || "theftPrevented",
      });

      if (existingReport.media_files && existingReport.media_files.length > 0) {
        setExistingFiles(existingReport.media_files);
      }
      setFilesToDelete([]);
    }
  }, [editMode, existingReport]);

  // ============ PROCESS UPLOAD QUEUE ============
  // This useEffect processes files from the queue one batch at a time
  // Prevents race conditions when multiple files are uploaded simultaneously
  useEffect(() => {
    const processQueue = async () => {
      // Don't process if already processing or queue is empty
      if (isProcessingQueue || uploadQueue.length === 0) return;
      
      console.log('🔄 Processing queue with', uploadQueue.length, 'files');
      setIsProcessingQueue(true);
      setIsUploading(true);
      
      // Take all files from queue
      const filesToProcess = [...uploadQueue];
      setUploadQueue([]); // Clear queue
      
      setIsCompressing(true);
      setCompressionProgress(0);
      
      const processedFiles = [];
      const initialProgress = {};
      const timestamp = Date.now(); // FIX: Generate timestamp once
      
      // Initialize progress for all files with consistent fileIds
      filesToProcess.forEach((file, index) => {
        const fileId = `${file.name}-${timestamp}-${index}`;
        initialProgress[fileId] = { 
          file, 
          progress: 0, 
          completed: false,
          compressing: true 
        };
      });
      setUploadProgress(initialProgress);
      
      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        const fileType = file.type.split('/')[0];
        const fileId = `${file.name}-${timestamp}-${i}`; // FIX: Use same timestamp
        
        try {
          // Update status: Compressing
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], progress: 10, compressing: true }
          }));
          
          let compressedFile;
          if (fileType === 'image') {
            setCompressionStatus(`Compressing image ${i + 1}/${filesToProcess.length}...`);
            compressedFile = await compressImage(file);
          } else if (fileType === 'video') {
            setCompressionStatus(`Compressing video ${i + 1}/${filesToProcess.length}... (may take a moment)`);
            compressedFile = await compressVideo(file);
          } else {
            compressedFile = file;
          }
          
          processedFiles.push(compressedFile);
          
          // Simulate upload progress after compression
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], progress: 50, compressing: false }
          }));
          
          // Simulate upload completion
          await new Promise(resolve => setTimeout(resolve, 300));
          
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { 
              file: compressedFile, 
              progress: 100, 
              completed: true,
              compressing: false 
            }
          }));
          
        } catch (error) {
          console.error(`Failed to process ${file.name}:`, error);
          processedFiles.push(file);
          
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { 
              ...prev[fileId], 
              progress: 100, 
              completed: true,
              error: true,
              compressing: false 
            }
          }));
        }
        
        const overallProgress = Math.round(((i + 1) / filesToProcess.length) * 100);
        if (fileType !== 'video') {
          setCompressionProgress(overallProgress);
        }
      }
      
      // Add all processed files at once - using functional update to ensure no race conditions
      setNewFiles((prev) => {
        console.log('✅ Adding files. Current:', prev.length, 'Adding:', processedFiles.length, 'New total:', prev.length + processedFiles.length);
        return [...prev, ...processedFiles];
      });
      
      // ============ NEW: Create preview URLs for each processed file ============
      setNewFilePreviewUrls((prevUrls) => {
        const newUrls = processedFiles.map((file) => URL.createObjectURL(file));
        return [...prevUrls, ...newUrls];
      });
      // ===========================================================================
      
      setIsCompressing(false);
      setCompressionProgress(0);
      setCompressionStatus('');
      
      // Clear upload progress after a short delay
      setTimeout(() => {
        setUploadProgress({});
        setIsUploading(false);
      }, 1500);
      
      setIsProcessingQueue(false);
    };
    
    processQueue();
  }, [uploadQueue, isProcessingQueue]);
  // =============================================

  // Handler for text / select changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "time" && value === "") {
      setFormData((prev) => ({ ...prev, [name]: null }));
    } else if (name === "announcement_by" && value === "") {
      setFormData((prev) => ({ ...prev, announcement_by: null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // NEW: Handle store selection from custom dropdown
  const handleStoreSelect = (storeId) => {
    setFormData((prev) => ({ ...prev, store: storeId }));
    setStoreDropdownOpen(false);
    setStoreSearch("");
  };

  // File handling
  // ============ IMAGE COMPRESSION ============
  const compressImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: 'image/jpeg'
      };
      
      
      const compressedBlob = await imageCompression(file, options);
      
      // IMPORTANT: Preserve the original filename by creating a new File object
      // The compression library sometimes returns a blob named "blob"
      const fileName = file.name.replace(/\.[^.]+$/, '.jpg'); // Change extension to .jpg since we're converting to JPEG
      const compressedFile = new File([compressedBlob], fileName, { 
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      const originalMB = (file.size / 1024 / 1024).toFixed(2);
      const compressedMB = (compressedFile.size / 1024 / 1024).toFixed(2);
      const reduction = Math.round((1 - compressedFile.size / file.size) * 100);
      
      console.log(`📷 Image: ${originalMB}MB → ${compressedMB}MB (${reduction}% reduction) - ${fileName}`);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      return file;
    }
  };

  // ============ VIDEO COMPRESSION ============
  const compressVideo = async (file) => {
    if (!ffmpegLoaded) {
      console.warn('⚠️ FFmpeg not loaded, skipping video compression');
      return file;
    }
    
    // Skip compression for files under 50MB (instant upload!)
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB < 50) {
      console.log(`⚡ File under 50MB (${fileSizeMB.toFixed(2)}MB), skipping compression`);
      return file;
    }
    
    try {
      const inputName = 'input.mp4';
      const outputName = 'output.mp4';
      
      await ffmpeg.writeFile(inputName, await fetchFile(file));
      
      // ULTRA-FAST SETTINGS - Speed prioritized
      await ffmpeg.exec([
        '-i', inputName,
        '-vcodec', 'libx264',
        '-crf', '28',              // Faster encoding (higher = faster but lower quality)
        '-preset', 'ultrafast',    // Absolute fastest preset!
        '-vf', 'scale=-2:720',     // 720p for speed
        '-acodec', 'aac',
        '-b:a', '128k',            // Lower audio for speed
        '-movflags', '+faststart',
        '-y',
        outputName
      ]);
      
      const data = await ffmpeg.readFile(outputName);
      const compressedBlob = new Blob([data.buffer], { type: 'video/mp4' });
      
      const compressedFile = new File(
        [compressedBlob],
        file.name.replace(/\.\w+$/, '.mp4'),
        { type: 'video/mp4', lastModified: Date.now() }
      );
      
      const originalMB = (file.size / 1024 / 1024).toFixed(2);
      const compressedMB = (compressedFile.size / 1024 / 1024).toFixed(2);
      const reduction = Math.round((1 - compressedFile.size / file.size) * 100);
      
      console.log(`🎥 Video: ${originalMB}MB → ${compressedMB}MB (${reduction}% reduction) - ${compressedFile.name}`);
      
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);
      
      return compressedFile;
    } catch (error) {
      console.error('❌ Video compression error:', error);
      return file;
    }
  };
  // ===========================================

  // ============ ENHANCED FILE HANDLER WITH QUEUE SYSTEM ============
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length === 0) return;
    
    // Add files to queue instead of processing immediately
    // This prevents race conditions when multiple uploads happen
    setUploadQueue(prev => {
      console.log('📥 Adding to queue:', selectedFiles.length, 'files');
      return [...prev, ...selectedFiles];
    });
    
    // Clear the input value to allow re-uploading the same file if needed
    e.target.value = '';
  };
  // ================================================================

  // handleUploadProgress function removed - now integrated into processQueue

  // ============ UPDATED: Delete handler must also clean preview URLs ============
  const handleNewFileDelete = (index) => {
    // Remove file
    setNewFiles((prevFiles) => {
      const updated = [...prevFiles];
      updated.splice(index, 1);
      return updated;
    });

    // Remove & revoke preview URL
    setNewFilePreviewUrls((prevUrls) => {
      const updated = [...prevUrls];
      const [urlToRevoke] = updated.splice(index, 1);
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke);
      }
      return updated;
    });
  };
  // =============================================================================

  const handleExistingFileDelete = (fileId) => {
    console.log('Marking file for deletion:', fileId);

    setFilesToDelete(prev => {
      if (!prev.includes(fileId)) {
        const updated = [...prev, fileId];
        console.log('Updated files to delete:', updated);
        return updated;
      }
      return prev;
    });

    setExistingFiles(prev => {
      const updated = prev.filter(file => file.id !== fileId);
      console.log('Remaining existing files:', updated);
      return updated;
    });
  };

  // Reset form
  const handleReset = () => {
    showModal(
      'confirm',
      'Reset Form',
      'Are you sure you want to reset the form? All unsaved changes will be lost.',
      () => {
        setFormData(initialFormData);
        setNewFiles([]);
        if (editMode && existingReport) {
          setExistingFiles(existingReport.media_files || []);
          setFilesToDelete([]);
        } else {
          setExistingFiles([]);
          setFilesToDelete([]);
        }
      },
      'Reset',
      'Cancel'
    );
  };

  // Handle back button
  const handleBack = () => {
    navigate(-1);
  };

  // Toggle action menu
  const toggleActionMenu = () => {
    setActionMenuOpen(!actionMenuOpen);
  };

  // Handle adding comment
  const handleAddComment = () => {
    if (!newComment.trim()) {
      showModal('error', 'Error', 'Please enter a comment');
      return;
    }

    dispatch(addReportCommentThunk({ reportId, text: newComment }))
      .unwrap()
      .then(() => {
        setNewComment("");
        showModal('success', 'Success', 'Comment added successfully');
      })
      .catch((err) => {
        showModal('error', 'Error', `Failed to add comment: ${err}`);
      });
  };

  // CREATE
  const handleSubmitCreate = (e) => {
    e.preventDefault();
    
    // ============ NEW: Prevent submitting while uploads/compression are in progress ============
    if (isUploading || isProcessingQueue || isCompressing) {
      showModal(
        'error',
        'Please wait',
        'Files are still being processed/uploaded. Please wait until the upload completes.'
      );
      return;
    }
    // ===========================================================================================

    if (!employeeId) {
      showModal('error', 'Error', 'No employee profile associated with your account!');
      return;
    }

    const formDataToSend = new FormData();

    formDataToSend.append('employee_id', employeeId);
    formDataToSend.append('store_id', formData.store);
    formDataToSend.append('incident_date', formData.incident_date);
    formDataToSend.append('incident_time', formData.incident_time || '');
    formDataToSend.append('details', formData.details);
    formDataToSend.append('call_status', formData.call_status);
    formDataToSend.append('reason_called', formData.reason_called || '');
    formDataToSend.append('reason_not_called', formData.reason_not_called || '');
    formDataToSend.append('severity', formData.severity);
    formDataToSend.append('announcement_by', formData.announcement_by || '');
    formDataToSend.append('customer_category', formData.customer_category || '');
    formDataToSend.append('dress', formData.dress || '');
    formDataToSend.append('product', formData.product || '');
    formDataToSend.append('pickup_time', formData.pickup_time || '');
    formDataToSend.append('hide_time', formData.hide_time || '');
    formDataToSend.append('exit_time', formData.exit_time || '');
    formDataToSend.append('status', 'SUBMITTED_REVIEW');
    formDataToSend.append('incident_type', formData.incident_type);

    newFiles.forEach((file, index) => {
      formDataToSend.append(`media_files`, file);
    });

    dispatch(createReportThunk(formDataToSend))
      .unwrap()
      .then(() => {
        showModal(
          'success',
          'Success!',
          'Report created successfully! You will be redirected to the reports list.',
          () => navigate("/"),
          'Continue'
        );
      })
      .catch((err) => {
        console.error("Failed to create report:", err);
        showModal('error', 'Error', 'Failed to create report. Please try again.');
      });
  };

  // UPDATE
  const handleUpdate = (newStatus) => {
    if (!existingReport) return;

    const statusText = newStatus.replace(/_/g, " ");

    showModal(
      'confirm',
      'Confirm Action',
      `Are you sure you want to update this report with status: "${statusText}"?`,
      () => {
        // ============ NEW: Prevent updating while uploads/compression are in progress ============
        if (isUploading || isProcessingQueue || isCompressing) {
          showModal(
            'error',
            'Please wait',
            'Files are still being processed/uploaded. Please wait until the upload completes.'
          );
          return;
        }
        // =========================================================================================
        
        console.log('Starting update process...');
        console.log('Files to delete:', filesToDelete);
        console.log('New files to add:', newFiles);

        // FIXED: Only use FormData if we have files to delete or new files to upload
        // Otherwise, use regular JSON to avoid multipart/form-data issues
        const hasFilesToDelete = filesToDelete.length > 0;
        const hasNewFiles = newFiles.length > 0;
        const needsFormData = hasFilesToDelete || hasNewFiles;

        let dataToSend;

        if (needsFormData) {
          // Use FormData when we need to handle file operations
          const formDataToSend = new FormData();

          formDataToSend.append('employee', employeeId);
          formDataToSend.append('store', formData.store);
          formDataToSend.append('incident_date', formData.incident_date);
          formDataToSend.append('incident_time', formData.incident_time || '');
          formDataToSend.append('details', formData.details);
          formDataToSend.append('call_status', formData.call_status);
          formDataToSend.append('reason_called', formData.reason_called || '');
          formDataToSend.append('reason_not_called', formData.reason_not_called || '');
          formDataToSend.append('severity', formData.severity);
          formDataToSend.append('announcement_by', formData.announcement_by || '');
          formDataToSend.append('customer_category', formData.customer_category || '');
          formDataToSend.append('dress', formData.dress || '');
          formDataToSend.append('product', formData.product || '');
          formDataToSend.append('pickup_time', formData.pickup_time || '');
          formDataToSend.append('hide_time', formData.hide_time || '');
          formDataToSend.append('exit_time', formData.exit_time || '');
          formDataToSend.append('status', newStatus || formData.status);
          formDataToSend.append('incident_type', formData.incident_type);

          // Only append new files if there are any
          if (hasNewFiles) {
            newFiles.forEach((file, index) => {
              formDataToSend.append(`media_files`, file);
            });
          }

          // Only append files to delete if there are any
          if (hasFilesToDelete) {
            console.log('Adding files to delete to FormData:', filesToDelete);
            formDataToSend.append('files_to_delete', JSON.stringify(filesToDelete));

            filesToDelete.forEach((fileId, index) => {
              formDataToSend.append(`delete_file_${index}`, fileId);
            });

            formDataToSend.append('delete_files', filesToDelete.join(','));
          }

          console.log('Using FormData - contents:');
          for (let [key, value] of formDataToSend.entries()) {
            console.log(key, value);
          }

          dataToSend = formDataToSend;
        } else {
          // Use regular JSON when no file operations are needed
          dataToSend = {
            employee: employeeId,
            store: formData.store,
            incident_date: formData.incident_date,
            incident_time: formData.incident_time || '',
            details: formData.details,
            call_status: formData.call_status,
            reason_called: formData.reason_called || '',
            reason_not_called: formData.reason_not_called || '',
            severity: formData.severity,
            announcement_by: formData.announcement_by || '',
            customer_category: formData.customer_category || '',
            dress: formData.dress || '',
            product: formData.product || '',
            pickup_time: formData.pickup_time || '',
            hide_time: formData.hide_time || '',
            exit_time: formData.exit_time || '',
            status: newStatus || formData.status,
            incident_type: formData.incident_type,
          };

          console.log('Using JSON - data:', dataToSend);
        }

        const payload = {
          reportId: existingReport.id,
          updates: dataToSend,
        };

        dispatch(updateReportThunk(payload))
          .unwrap()
          .then((updatedReport) => {
            console.log('Update successful:', updatedReport);
            setFilesToDelete([]);

            showModal(
              'success',
              'Success!',
              `Report updated successfully! Status: ${statusText}`,
              () => navigate("/"),
              'Continue'
            );
          })
          .catch((err) => {
            console.error("Failed to update report:", err);
            showModal('error', 'Error', `Failed to update report. Error: ${err.message || err}`);
          });
      },
      'Update',
      'Cancel'
    );
  };

  // If loading data in edit mode
  if (reportsLoading && editMode && !existingReport) {
    return (
      <div className="incident-report-app">
        <Sidebar />
        <div className="incident-report-content">
          <div className="incident-report-loading">
            <div className="incident-report-loader"></div>
            <p>Loading Report Data...</p>
          </div>
        </div>
      </div>
    );
  }

  // PA users cannot edit reports - redirect them
  if (editMode && userIsPA) {
    return (
      <div className="incident-report-app">
        <Sidebar />
        <div className="incident-report-content">
          <div className="incident-report-container">
            <div className="incident-report-error-message">
              <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 style={{ color: '#991b1b', marginBottom: '12px', fontSize: '24px' }}>
                  Access Denied
                </h2>
                <p style={{ color: '#7f1d1d', marginBottom: '24px', fontSize: '16px' }}>
                  PA users cannot edit reports. You can only add comments to reports.
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Go Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="incident-report-content">
      {/* Confirmation Modal */}
      {modalState.isOpen && (
        <div className="incident-report-modal-overlay">
          <div className="incident-report-modal">
            <div className="incident-report-modal-header">
              <div className={`incident-report-modal-icon incident-report-modal-icon-${modalState.type}`}>
                {modalState.type === 'success' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {modalState.type === 'error' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {modalState.type === 'confirm' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.228 9C8.337 7.8 9.22 7 10.5 7H13.5C14.78 7 15.663 7.8 15.772 9M8.228 9L8.228 9C8.228 9 8.228 9 8.228 9ZM8.228 9C8.337 10.2 9.22 11 10.5 11H13.5C14.78 11 15.663 10.2 15.772 9M15.772 9C15.772 9 15.772 9 15.772 9V9ZM15.772 9L15.772 9ZM7 9H17M10 3V7M14 3V7M7 9V20C7 20.5523 7.44772 21 8 21H16C16.5523 21 17 20.5523 17 20V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <h3 className="incident-report-modal-title">{modalState.title}</h3>
            </div>

            <div className="incident-report-modal-body">
              <p className="incident-report-modal-message">{modalState.message}</p>
            </div>

            <div className="incident-report-modal-footer">
              {modalState.type === 'confirm' && (
                <button
                  className="incident-report-modal-btn incident-report-modal-btn-secondary"
                  onClick={closeModal}
                >
                  {modalState.cancelText}
                </button>
              )}
              <button
                className={`incident-report-modal-btn ${modalState.type === 'error'
                    ? 'incident-report-modal-btn-danger'
                    : 'incident-report-modal-btn-primary'
                  }`}
                onClick={modalState.type === 'confirm' ? handleModalConfirm : closeModal}
              >
                {modalState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Notes Modal */}
      {showStoreNotesModal && (
        <div className="incident-report-modal-overlay" onClick={handleCloseStoreNotesModal}>
          <div className="incident-report-store-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incident-report-store-notes-header">
              <div>
                <h3>Store Notes</h3>
                {selectedStore && (
                  <p className="incident-report-store-notes-subtitle">
                    {selectedStore.store_name} - {selectedStore.store_city}
                  </p>
                )}
              </div>
              <button
                className="incident-report-store-notes-close"
                onClick={handleCloseStoreNotesModal}
              >
                Ã—
              </button>
            </div>

            <div className="incident-report-store-notes-body">
              {notesLoading ? (
                <div className="incident-report-store-notes-loading">
                  <div className="incident-report-loader"></div>
                  <p>Loading notes...</p>
                </div>
              ) : storeNotes && storeNotes.notes && storeNotes.notes.length > 0 ? (
                <div className="incident-report-store-notes-list">
                  {storeNotes.notes.map((note) => (
                    <div
                      key={note.id}
                      className={`incident-report-store-note-item ${note.is_important ? 'important' : ''
                        }`}
                    >
                      {note.is_important && (
                        <div className="incident-report-note-important-badge">
                          Important
                        </div>
                      )}
                      <p className="incident-report-note-text">{note.note_text}</p>
                      <div className="incident-report-note-meta">
                        <span>By: {note.created_by_name}</span>
                        <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="incident-report-store-notes-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12H15M12 9V15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p>No notes available for this store</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="incident-report-layout">
        {/* Left side - Image */}
        <div className="incident-report-image-side">
          <div className="incident-report-image-content">
          </div>
          <button
            type="button"
            className="incident-report-back-btn"
            onClick={handleBack}
          >
             Back
          </button>
        </div>

        {/* Right side - Form */}
        <div className="incident-report-form-side">
          <div className="incident-report-container">
            <div className="incident-report-header">
              <h1>{editMode ? "Edit Incident Report" : "Create Incident Report"}</h1>
              
              {/* PA User Warning Banner for Create Mode */}
              {!editMode && userIsPA && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#d97706', flexShrink: 0 }}>
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#92400e', fontWeight: '500', fontSize: '14px', margin: 0 }}>
                      Note: As a PA user, you cannot upload media files with your reports.
                    </p>
                  </div>
                </div>
              )}
              
              {editMode && filesToDelete.length > 0 && (
                <div style={{ background: '#ffe6e6', padding: '8px', marginTop: '8px', borderRadius: '4px', fontSize: '12px' }}>
                  Files marked for deletion: {filesToDelete.join(', ')}
                </div>
              )}
            </div>

            <form onSubmit={editMode ? undefined : handleSubmitCreate}>
              {/* ... All existing form sections remain exactly the same ... */}
              {/* What's your incident like? */}
              <div className="incident-report-section">
                <h2>What's your incident like?</h2>

                <div className="incident-report-group">
                  <label htmlFor="incident_type">What type of incident is this?</label>
                  <div className="incident-report-select-wrapper">
                    <select
                      id="incident_type"
                      name="incident_type"
                      className="incident-report-select"
                      value={formData.incident_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="theftPrevented">Theft Prevented</option>
                      <option value="theftReported">Theft Reported</option>
                      <option value="incidentReported">Incident Reported</option>
                      <option value="customerDenied">Customer Denied</option>
                      <option value="cashierSuspicious">Cashier Suspicious</option>
                      <option value="openingClosing">Opening/Closing</option>
                      <option value="announcement">Announcement</option>
                    </select>
                  </div>
                </div>

                <div className="incident-report-group">
                  <label htmlFor="incident_date">When did this incident occur?</label>
                  <input
                    id="incident_date"
                    type="date"
                    name="incident_date"
                    className="incident-report-date"
                    value={formData.incident_date}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="incident-report-group">
                  <label htmlFor="incident_time">At what time?</label>
                  <input
                    id="incident_time"
                    type="time"
                    name="incident_time"
                    className="incident-report-time"
                    value={formData.incident_time ?? ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Custom Store Selector with Search and Info Button */}
                <div className="incident-report-group">
                  <label htmlFor="store">
                    Where will your incident be located?
                    {formData.store && (
                      <button
                        type="button"
                        className="incident-report-store-info-btn"
                        onClick={handleShowStoreNotes}
                        title="View store notes"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        View Notes
                      </button>
                    )}
                  </label>

                  <div className="incident-report-custom-select" ref={storeDropdownRef}>
                    <div
                      className="incident-report-custom-select-trigger"
                      onClick={() => setStoreDropdownOpen(!storeDropdownOpen)}
                    >
                      <span>
                        {selectedStore ? selectedStore.store_name : 'Select store/location'}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`incident-report-select-arrow ${storeDropdownOpen ? 'open' : ''}`}
                      >
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>

                    {storeDropdownOpen && (
                      <div className="incident-report-custom-select-dropdown">
                        <div className="incident-report-store-search">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search stores..."
                            value={storeSearch}
                            onChange={(e) => setStoreSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div className="incident-report-store-options">
                          {storesLoading ? (
                            <div className="incident-report-store-option loading">
                              Loading stores...
                            </div>
                          ) : filteredStores.length > 0 ? (
                            filteredStores.map((store) => (
                              <div
                                key={store.id}
                                className={`incident-report-store-option ${formData.store === store.id ? 'selected' : ''
                                  }`}
                                onClick={() => handleStoreSelect(store.id)}
                              >
                                <div className="incident-report-store-option-main">
                                  <span className="incident-report-store-name">{store.store_name}</span>
                                  {store.store_city && (
                                    <span className="incident-report-store-city">{store.store_city}</span>
                                  )}
                                </div>
                                {formData.store === store.id && (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="incident-report-store-option empty">
                              No stores found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="incident-report-group">
                  <label htmlFor="severity">How severe was this incident?</label>
                  <div className="incident-report-select-wrapper">
                    <select
                      id="severity"
                      name="severity"
                      className="incident-report-select"
                      value={formData.severity}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select severity</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* How would you like to report? */}
              <div className="incident-report-section">
                <h2>How would you like to report?</h2>

                <div className="incident-report-group">
                  <label>Call Status:</label>
                  <div className="incident-report-radio-options">
                    <label className="incident-report-radio-option">
                      <input
                        type="radio"
                        name="call_status"
                        value="CALLED"
                        checked={formData.call_status === "CALLED"}
                        onChange={handleChange}
                        required
                      />
                      <div className="incident-report-radio-content">
                        <div className="incident-report-radio-check"></div>
                        <div>
                          <span className="incident-report-radio-title">Called</span>
                          <p className="incident-report-radio-description">The incident was reported via call</p>
                        </div>
                      </div>
                    </label>

                    <label className="incident-report-radio-option">
                      <input
                        type="radio"
                        name="call_status"
                        value="NOT_CALLED"
                        checked={formData.call_status === "NOT_CALLED"}
                        onChange={handleChange}
                      />
                      <div className="incident-report-radio-content">
                        <div className="incident-report-radio-check"></div>
                        <div>
                          <span className="incident-report-radio-title">Not Called</span>
                          <p className="incident-report-radio-description">The incident was not reported via call</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {formData.call_status === "CALLED" && (
                  <div className="incident-report-group">
                    <label htmlFor="reason_called">Reason for Called:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="reason_called"
                        name="reason_called"
                        className="incident-report-select"
                        value={formData.reason_called}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select reason</option>
                        {reasonCalledOptions.map((option, index) => (
                          <option value={option} key={index}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {formData.call_status === "NOT_CALLED" && (
                  <div className="incident-report-group">
                    <label htmlFor="reason_not_called">Reason for Not Called:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="reason_not_called"
                        name="reason_not_called"
                        className="incident-report-select"
                        value={formData.reason_not_called}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select reason</option>
                        {reasonNotCalledOptions.map((option, index) => (
                          <option value={option} key={index}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Details for High Severity */}
              {formData.severity === "high" && (
                <div className="incident-report-section">
                  <h2>Additional Details</h2>

                  <div className="incident-report-group">
                    <label>Customer Category:</label>
                    <div className="incident-report-radio-options">
                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="male"
                          checked={formData.customer_category === "male"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Male</span>
                        </div>
                      </label>

                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="female"
                          checked={formData.customer_category === "female"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Female</span>
                        </div>
                      </label>

                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="kid"
                          checked={formData.customer_category === "kid"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Kid</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="dress">Description of Dress:</label>
                    <input
                      type="text"
                      id="dress"
                      name="dress"
                      className="incident-report-input"
                      value={formData.dress}
                      onChange={handleChange}
                      placeholder="Color and type of clothing..."
                    />
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="product">Product Involved:</label>
                    <input
                      type="text"
                      id="product"
                      name="product"
                      className="incident-report-input"
                      value={formData.product}
                      onChange={handleChange}
                      placeholder="Product name or description..."
                    />
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="announcement_by">Announcement By:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="announcement_by"
                        name="announcement_by"
                        className="incident-report-select"
                        value={formData.announcement_by ?? ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Employee</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.full_name || `${employee.user.first_name} ${employee.user.last_name}`}
                          </option>
                        ))}
                      </select>
                      {employeesLoading && <span className="incident-report-loading-indicator">Loading employees...</span>}
                    </div>
                  </div>

                  <div className="incident-report-timeline">
                    <h3>Incident Timeline</h3>
                    <div className="incident-report-timeline-fields">
                      <div className="incident-report-group">
                        <label htmlFor="pickup_time">Pickup Time:</label>
                        <input
                          id="pickup_time"
                          name="pickup_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.pickup_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="incident-report-group">
                        <label htmlFor="hide_time">Hide Time:</label>
                        <input
                          id="hide_time"
                          name="hide_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.hide_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="incident-report-group">
                        <label htmlFor="exit_time">Exit Time:</label>
                        <input
                          id="exit_time"
                          name="exit_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.exit_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Incident Description */}
              <div className="incident-report-section">
                <h2>Incident Description</h2>
                <div className="incident-report-group">
                  <label htmlFor="details">Please describe what happened:</label>
                  <textarea
                    id="details"
                    name="details"
                    className="incident-report-textarea"
                    placeholder="Provide a detailed description of what happened..."
                    value={formData.details}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Evidence & Documentation */}
              <div className="incident-report-section">
                <h2>Evidence & Documentation</h2>

                {/* Display existing files from server */}
                {existingFiles.length > 0 && (
                  <div className="incident-report-file-list">
                    <h3>Existing Files ({existingFiles.length})</h3>
                    <ul>
                      {existingFiles.map((file) => (
                        <li key={`existing-${file.id}`} className="incident-report-file-item">
                        <a  
                            href={file.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="incident-report-file-link"
                          >
                            {getFileTypeFromUrl(file.file) === 'image' ? (
                              <img
                                src={file.file}
                                alt="existing file preview"
                                className="incident-report-file-preview"
                              />
                            ) : getFileTypeFromUrl(file.file) === 'video' ? (
                              <div className="incident-report-file-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M15 10L19.553 7.724C20.0049 7.49787 20.5256 7.43298 21.0197 7.54147C21.5138 7.64996 21.9501 7.9247 22.2472 8.31858C22.5443 8.71246 22.6837 9.19876 22.6399 9.68743C22.5961 10.1761 22.3721 10.6323 22.01 10.967L22 11V13C22 13.79 21.6 14.53 21 15L15 18.5V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M2 7C2 5.89543 2.89543 5 4 5H13C14.1046 5 15 5.89543 15 7V17C15 18.1046 14.1046 19 13 19H4C2.89543 19 2 18.1046 2 17V7Z" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                              </div>
                            ) : (
                              <div className="incident-report-file-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            )}
                            <span className="incident-report-file-name">
                              {getFileNameFromUrl(file.file)}
                            </span>
                          </a>
                          <button
                            type="button"
                            className="incident-report-delete-file"
                            onClick={() => handleExistingFileDelete(file.id)}
                            aria-label="Delete file"
                            title={`Delete file (ID: ${file.id})`}
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* File upload for new files */}
                <div className="incident-report-file-container">
                
                {/* ============ QUEUE DISPLAY ============ */}
                {uploadQueue.length > 0 && !isProcessingQueue && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #22c55e',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#22c55e',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {uploadQueue.length}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#15803d'
                      }}>
                        {uploadQueue.length} file{uploadQueue.length > 1 ? 's' : ''} queued
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#166534',
                        marginTop: '2px'
                      }}>
                        Will be processed after current compression completes
                      </div>
                    </div>
                  </div>
                )}
                {/* ======================================= */}
                
                {/* ============ COMPRESSION PROGRESS INDICATOR ============ */}
                {isCompressing && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid #e0f2fe',
                      borderTop: '3px solid #0ea5e9',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#0369a1',
                        marginBottom: '4px'
                      }}>
                        {compressionStatus}
                      </div>
                      {compressionProgress > 0 && (
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#e0f2fe',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${compressionProgress}%`,
                            height: '100%',
                            backgroundColor: '#0ea5e9',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* ======================================================== */}                  {/* File Upload Section - Available for SPA+ in both create and edit modes */}
                  {userIsPA ? (
                    /* PA Users: Show warning - cannot upload in any mode */
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, color: '#d97706' }}>
                        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div>
                        <p style={{ color: '#92400e', fontWeight: '500', marginBottom: '4px' }}>
                          File uploads not available
                        </p>
                        <p style={{ color: '#78350f', fontSize: '14px' }}>
                          PA users cannot upload media files. Please fill in all other required fields.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* SPA and above: Show file upload in both create and edit modes */
                    <label className="incident-report-file-upload">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="incident-report-file-input"
                      />
                      <div className="incident-report-upload-content">
                        <div className="incident-report-upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        </div>
                        <div className="incident-report-upload-text">
                          <span>Upload {existingFiles.length > 0 ? 'additional ' : ''}files</span>
                          <p>Click to browse or drag files here</p>
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Upload Progress Indicators */}
                {Object.keys(uploadProgress).length > 0 && (
                  <div className="incident-report-upload-progress-section">
                    <h3>Uploading Files...</h3>
                    <div className="incident-report-progress-list">
                      {Object.entries(uploadProgress).map(([fileId, data]) => (
                        <div key={fileId} className="incident-report-progress-item">
                          <div className="incident-report-progress-info">
                            <span className="incident-report-progress-filename">
                              {data.file.name}
                            </span>
                            <span className="incident-report-progress-percentage">
                              {Math.round(data.progress)}%
                            </span>
                          </div>
                          <div className="incident-report-progress-bar">
                            <div 
                              className="incident-report-progress-fill"
                              style={{ width: `${data.progress}%` }}
                            ></div>
                          </div>
                          {data.completed && (
                            <div className="incident-report-progress-complete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>Complete</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Display newly uploaded files */}
                {newFiles.length > 0 && (
                  <div className="incident-report-file-list">
                    <h3>New Files ({newFiles.length})</h3>
                    <ul>
                      {newFiles.map((file, index) => {
                        const previewUrl = newFilePreviewUrls[index];

                        return (
                          <li key={`new-${index}`} className="incident-report-file-item">
                            {previewUrl ? (
                              <a
                                href={previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="incident-report-file-link"
                                download={file.name}
                              >
                                {file.type?.startsWith("image/") ? (
                                  <img
                                    src={previewUrl}
                                    alt={file.name || "preview"}
                                    className="incident-report-file-preview"
                                  />
                                ) : file.type?.startsWith("video/") ? (
                                  <div className="incident-report-file-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M15 10L19.553 7.724C20.0049 7.49787 20.5256 7.43298 21.0197 7.54147C21.5138 7.64996 21.9501 7.9247 22.2472 8.31858C22.5443 8.71246 22.6837 9.19876 22.6399 9.68743C22.5961 10.1761 22.3721 10.6323 22.01 10.967L22 11V13C22 13.79 21.6 14.53 21 15L15 18.5V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M2 7C2 5.89543 2.89543 5 4 5H13C14.1046 5 15 5.89543 15 7V17C15 18.1046 14.1046 19 13 19H4C2.89543 19 2 18.1046 2 17V7Z" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="incident-report-file-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                )}
                                <span className="incident-report-file-name">
                                  {file.name}
                                </span>
                              </a>
                            ) : (
                              <div className="incident-report-file-link">
                                <div className="incident-report-file-icon">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                                <span className="incident-report-file-name">
                                  {file.name}
                                </span>
                              </div>
                            )}

                            <button
                              type="button"
                              className="incident-report-delete-file"
                              onClick={() => handleNewFileDelete(index)}
                              aria-label="Delete file"
                              title={`Delete file: ${file.name}`}
                            >
                              &times;
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Comments Section - Only visible in edit mode - UPDATED WITH MENTIONS */}
              {editMode && (
                <div className="incident-report-section">
                  <h2>Comments</h2>

                  {/* Add new comment with mention support */}
                  <div className="incident-report-comment-input-container">
                    <div className="incident-report-comment-input-wrapper">
                      <div className="incident-report-mention-wrapper">
                        <textarea
                          ref={commentInputRef}
                          className="incident-report-comment-input"
                          placeholder="Add a comment... (Type @ to mention someone)"
                          value={newComment}
                          onChange={handleCommentChange}
                          rows="3"
                        />
                        
                        {/* Mention dropdown */}
                        {showMentionDropdown && (
                          <div 
                            ref={mentionDropdownRef}
                            className="incident-report-mention-dropdown"
                          >
                            <div className="incident-report-mention-dropdown-header">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>Select employee to mention</span>
                            </div>
                            <div className="incident-report-mention-list">
                              {filteredEmployeesForMention.length > 0 ? (
                                filteredEmployeesForMention.map((emp) => (
                                  <div
                                    key={emp.id}
                                    className="incident-report-mention-item"
                                    onClick={() => handleMentionSelect(emp)}
                                  >
                                    {emp.image ? (
                                      <img
                                        src={emp.image}
                                        alt={emp.user?.first_name}
                                        className="incident-report-mention-avatar"
                                      />
                                    ) : (
                                      <div className="incident-report-mention-avatar-placeholder">
                                        {emp.user?.first_name?.charAt(0) || 'U'}
                                      </div>
                                    )}
                                    <div className="incident-report-mention-info">
                                      <span className="incident-report-mention-name">
                                        {`${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim()}
                                      </span>
                                      {emp.role && (
                                        <span className="incident-report-mention-role">
                                          {emp.role}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="incident-report-mention-empty">
                                  No employees found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <button
                        type="button"
                        className="incident-report-comment-submit"
                        onClick={handleAddComment}
                        disabled={addingComment || !newComment.trim()}
                      >
                        {addingComment ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>
                    {addCommentError && (
                      <div className="incident-report-comment-error">
                        {addCommentError}
                      </div>
                    )}
                  </div>

                  {/* Display comments with mentions */}
                  <div className="incident-report-comments-list">
                    {commentsState.loading && (
                      <div className="incident-report-comments-loading">
                        <div className="incident-report-loader"></div>
                        <p>Loading comments...</p>
                      </div>
                    )}

                    {commentsState.error && (
                      <div className="incident-report-comments-error">
                        Failed to load comments: {commentsState.error}
                      </div>
                    )}

                    {!commentsState.loading && commentsState.comments.length === 0 && (
                      <div className="incident-report-no-comments">
                        <p>No comments yet. Be the first to comment!</p>
                      </div>
                    )}

                    {!commentsState.loading && commentsState.comments.length > 0 && (
                      <div className="incident-report-comments">
                        {commentsState.comments.map((comment) => (
                          <div key={comment.id} className="incident-report-comment">
                            <div className="incident-report-comment-header">
                              <div className="incident-report-comment-author">
                                {comment.commented_by.image ? (
                                  <img
                                    src={comment.commented_by.image}
                                    alt={comment.commented_by.name}
                                    className="incident-report-comment-avatar"
                                  />
                                ) : (
                                  <div className="incident-report-comment-avatar-placeholder">
                                    {comment.commented_by.name.charAt(0)}
                                  </div>
                                )}
                                <div className="incident-report-comment-author-info">
                                  <span className="incident-report-comment-author-name">
                                    {comment.commented_by.name}
                                  </span>
                                  {comment.commented_by.role && (
                                    <span className="incident-report-comment-author-role">
                                      {comment.commented_by.role}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="incident-report-comment-date">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="incident-report-comment-body">
                              {parseCommentText(comment.text).map((part, index) => {
                                if (part.type === 'mention') {
                                  return (
                                    <span key={index} className="incident-report-comment-mention">
                                      @{part.name}
                                    </span>
                                  );
                                }
                                return <span key={index}>{part.content}</span>;
                              })}
                            </div>
                            {comment.mentioned_employees && comment.mentioned_employees.length > 0 && (
                              <div className="incident-report-comment-mentions-list">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Mentioned: {comment.mentioned_employees.map(e => e.name).join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="incident-report-actions">
                {!editMode ? (
                  <>
                    <button
                      type="button"
                      className="incident-report-btn-secondary"
                      onClick={handleReset}
                      disabled={isUploading || isProcessingQueue || isCompressing}
                    >
                      Reset Form
                    </button>
                    <div className="incident-report-right-actions">
                      <button 
                        type="submit" 
                        className="incident-report-btn-primary"
                        disabled={isUploading || isProcessingQueue || isCompressing}
                      >
                        {(isUploading || isProcessingQueue || isCompressing) ? 'Processing Files...' : 'Create Report'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="incident-report-status-actions">
                      <div className="incident-report-status-display">
                        <strong>Current Status:</strong>
                        <span className={`incident-report-status-badge ${getStatusBadgeClass(formData.status)}`}>
                          {formData.status.replace(/_/g, " ")}
                        </span>
                      </div>

                      <div className="incident-report-action-dropdown" ref={actionMenuRef}>
                        <button
                          type="button"
                          className="incident-report-action-button incident-report-take-action-btn"
                          onClick={toggleActionMenu}
                          disabled={isUploading || isProcessingQueue || isCompressing}
                        >
                          {(isUploading || isProcessingQueue || isCompressing) ? 'Processing Files...' : 'Take Action'}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`incident-report-dropdown-arrow ${actionMenuOpen ? 'open' : ''}`}
                          >
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        {actionMenuOpen && !(isUploading || isProcessingQueue || isCompressing) && (
                          <div className="incident-report-dropdown-menu">
                            {/* Submit for Approval - Available to SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-submit"
                                onClick={() => {
                                  handleUpdate("SUBMITTED_APPROVAL");
                                  setActionMenuOpen(false);
                                }}
                              >
                                Submit for Approval
                              </button>
                            )}
                            
                            {/* Approve - Only Team Leader and above */}
                            {userIsTeamLeaderOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-approve"
                                onClick={() => {
                                  handleUpdate("APPROVED");
                                  setActionMenuOpen(false);
                                }}
                              >
                                Approve
                              </button>
                            )}
                            
                            {/* Decline - SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-decline"
                                onClick={() => {
                                  handleUpdate("DECLINED");
                                  setActionMenuOpen(false);
                                }}
                              >
                                Decline
                              </button>
                            )}
                            
                            {/* Mark Discrepancy - SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-warning"
                                onClick={() => {
                                  handleUpdate("DISCREPANCY");
                                  setActionMenuOpen(false);
                                }}
                              >
                                Mark Discrepancy
                              </button>
                            )}
                            
                            {/* Update (Keep Status) - SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-update"
                                onClick={() => {
                                  handleUpdate(formData.status);
                                  setActionMenuOpen(false);
                                }}
                              >
                                Update (Keep Status)
                              </button>
                            )}
                            
                            {/* Show message if user has no permissions */}
                            {!userIsSPAOrAbove && (
                              <div style={{
                                padding: '12px',
                                color: '#6b7280',
                                fontSize: '14px',
                                textAlign: 'center'
                              }}>
                                You don't have permission to take actions on this report.
                                You can add comments below.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateEditReportForm;